import type {
  ColumnDef,
  OnChangeFn,
  SortingState,
  Table as TableType,
  VisibilityState,
} from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { DataTablePagination } from "./data-table-pagination";
import { cn } from "./utils";

interface DataTableProps<TData extends { id?: string | number }, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onSortingChange?: OnChangeFn<SortingState>;
  sorting?: SortingState;
  manualSorting?: boolean;
  manualPagination?: boolean;
  pageCount?: number;
  toolbar?: React.ReactNode | ((table: TableType<TData>) => React.ReactNode);
  pagination?: boolean;
  initialPageSize?: number;
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
}

export function DataTable<TData extends { id?: string | number }, TValue>({
  columns,
  data,
  onSortingChange,
  sorting: controlledSorting,
  manualSorting = false,
  manualPagination = false,
  pageCount,
  toolbar,
  pagination = true,
  initialPageSize = 25,
  onRowClick,
  rowClassName,
}: DataTableProps<TData, TValue>) {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const sorting = controlledSorting ?? internalSorting;
  const setSorting = onSortingChange ?? setInternalSorting;

  const table = useReactTable({
    data,
    columns,
    getRowId: (row: TData) => row.id?.toString() ?? Math.random().toString(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: !manualPagination
      ? getPaginationRowModel()
      : undefined,
    getSortedRowModel: !manualSorting ? getSortedRowModel() : undefined,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    manualSorting,
    manualPagination,
    pageCount,
    initialState: {
      pagination: {
        pageSize: initialPageSize,
      },
    },
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="flex flex-col gap-4">
      {toolbar && (typeof toolbar === "function" ? toolbar(table) : toolbar)}
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={cn(
                    onRowClick &&
                      "cursor-pointer transition-colors hover:bg-muted/60",
                    rowClassName ? rowClassName(row.original) : undefined,
                  )}
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? "button" : undefined}
                  onClick={(event) => {
                    if (!onRowClick) return;
                    const target = event.target as HTMLElement;
                    const currentTarget = event.currentTarget as HTMLElement;
                    const interactive = target.closest(
                      "a,button,input,textarea,select,label,[role='button']",
                    );
                    if (interactive && interactive !== currentTarget) {
                      return;
                    }
                    onRowClick(row.original);
                  }}
                  onKeyDown={(event) => {
                    if (!onRowClick) return;
                    if (event.key !== "Enter" && event.key !== " ") return;
                    const target = event.target as HTMLElement;
                    const currentTarget = event.currentTarget as HTMLElement;
                    const interactive = target.closest(
                      "a,button,input,textarea,select,label,[role='button']",
                    );
                    if (interactive && interactive !== currentTarget) {
                      return;
                    }
                    event.preventDefault();
                    onRowClick(row.original);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No hay resultados.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {pagination && !manualPagination && <DataTablePagination table={table} />}
    </div>
  );
}
